import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Send, AlertCircle, Clock, RefreshCw, MessageSquare, Wifi, WifiOff } from 'lucide-react';
import { getFooterText } from '@/config/version';
import { Client, Conversation, Message as TwilioMessage, Participant, ConnectionState, State } from '@twilio/conversations';
import { API_SERVER_URL } from '@/lib/apiConfig';

// API base URL - messaging is part of the main API
const API_URL = API_SERVER_URL;

interface Message {
  id: string;
  body: string;
  authorId: string;
  authorType: 'patient' | 'provider' | 'system';
  createdAt: string;
}

interface SessionInfo {
  expiresAt: string;
  expiringSoon: boolean;
}

interface ConversationInfo {
  id: string;
  twilioSid?: string;
  type: string;
  status: string;
  patientName?: string;
  patientId?: string;
}

interface ParticipantInfo {
  identity: string;
  attributes?: {
    role?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
  };
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'failed';

// Auto-reconnect configuration
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff

const PatientChat = () => {
  const { token } = useParams<{ token: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  
  const [conversation, setConversation] = useState<ConversationInfo | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [participants, setParticipants] = useState<Map<string, ParticipantInfo>>(new Map());
  
  // Twilio real-time state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [typingIndicator, setTypingIndicator] = useState(false);
  const twilioClientRef = useRef<Client | null>(null);
  const twilioConversationRef = useRef<Conversation | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  
  // Auto-reconnect state
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const twilioSidRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Map Twilio connection state to our status
  const mapConnectionState = useCallback((state: ConnectionState | State | string): ConnectionStatus => {
    switch (state) {
      case 'connecting':
        return 'connecting';
      case 'connected':
      case 'initialized':
        return 'connected';
      case 'disconnecting':
      case 'disconnected':
        return 'disconnected';
      case 'denied':
      case 'failed':
        return 'failed';
      default:
        return 'disconnected';
    }
  }, []);

  // Cleanup Twilio resources
  const cleanupTwilio = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    twilioClientRef.current = null;
    twilioConversationRef.current = null;
  }, []);

  // Initialize Twilio client for real-time messaging
  const initializeTwilio = useCallback(async (twilioSid: string): Promise<boolean> => {
    if (!token) return false;

    // Store for reconnection
    twilioSidRef.current = twilioSid;

    try {
      setConnectionStatus('connecting');

      // Fetch Twilio access token
      const tokenRes = await fetch(`${API_URL}/api/messaging/patient/${token}/twilio-token`);
      
      if (!tokenRes.ok) {
        console.error('[PatientChat] Failed to get Twilio token');
        throw new Error('Failed to get access token');
      }

      const tokenData = await tokenRes.json();
      
      // Initialize Twilio client
      const client = new Client(tokenData.token);
      twilioClientRef.current = client;

      // Wait for client initialization
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 30000);
        
        client.on('stateChanged', (state: State) => {
          const mappedState = mapConnectionState(state);
          if (mappedState !== 'reconnecting') {
            setConnectionStatus(mappedState);
          }
          
          if (state === 'initialized') {
            clearTimeout(timeout);
            resolve();
          } else if (state === 'failed') {
            clearTimeout(timeout);
            reject(new Error('Client initialization failed'));
          }
        });

        // Monitor connection state for disconnections
        client.on('connectionStateChanged', (state: ConnectionState) => {
          console.log('[PatientChat] Connection state changed:', state);
          if (state === 'disconnected' || state === 'denied') {
            console.warn('[PatientChat] Connection lost, scheduling reconnect');
            // Trigger reconnection
            scheduleReconnect();
          } else if (state === 'connected') {
            console.log('[PatientChat] WebSocket connected successfully');
          }
        });

        // Monitor client-level events
        client.on('conversationAdded', (conversation: Conversation) => {
          console.log('[PatientChat] Conversation added to client:', conversation.sid);
        });

        client.on('conversationUpdated', (data: { conversation: Conversation; updateReasons: any[] }) => {
          console.log('[PatientChat] Conversation updated:', data.conversation.sid);
        });

        // Check current connection state
        if (client.connectionState === 'connected') {
          clearTimeout(timeout);
          resolve();
        }
      });

      // Handle token refresh
      client.on('tokenAboutToExpire', async () => {
        console.log('[PatientChat] Token about to expire, refreshing...');
        try {
          const refreshRes = await fetch(`${API_URL}/api/messaging/patient/${token}/twilio-token`);
          if (refreshRes.ok) {
            const newTokenData = await refreshRes.json();
            await client.updateToken(newTokenData.token);
            console.log('[PatientChat] Token refreshed');
          }
        } catch (err) {
          console.error('[PatientChat] Failed to refresh token:', err);
        }
      });

      client.on('tokenExpired', async () => {
        console.log('[PatientChat] Token expired, attempting refresh...');
        try {
          const refreshRes = await fetch(`${API_URL}/api/messaging/patient/${token}/twilio-token`);
          if (refreshRes.ok) {
            const newTokenData = await refreshRes.json();
            await client.updateToken(newTokenData.token);
          }
        } catch (err) {
          console.error('[PatientChat] Failed to refresh expired token:', err);
          scheduleReconnect();
        }
      });

      // Get the conversation
      const twilioConv = await client.getConversationBySid(twilioSid);
      twilioConversationRef.current = twilioConv;

      console.log('[PatientChat] Got conversation:', {
        sid: twilioConv.sid,
        friendlyName: twilioConv.friendlyName,
        status: twilioConv.status,
        dateCreated: twilioConv.dateCreated,
        state: twilioConv.state?.current,
      });

      // Get and store participant information for avatar display
      try {
        const participantsList = await twilioConv.getParticipants();
        const currentIdentity = client.user?.identity;
        console.log('[PatientChat] Conversation participants:', {
          count: participantsList.length,
          identities: participantsList.map(p => p.identity),
          currentIdentity,
          isParticipant: participantsList.some(p => p.identity === currentIdentity),
        });
        
        // Store participant info for avatar display
        const participantsMap = new Map<string, ParticipantInfo>();
        for (const participant of participantsList) {
          try {
            const attrs = participant.attributes ? JSON.parse(participant.attributes) : {};
            participantsMap.set(participant.identity || '', {
              identity: participant.identity || '',
              attributes: attrs,
            });
          } catch (e) {
            // If attributes can't be parsed, just store identity
            participantsMap.set(participant.identity || '', {
              identity: participant.identity || '',
            });
          }
        }
        setParticipants(participantsMap);
      } catch (err) {
        console.warn('[PatientChat] Could not fetch participants:', err);
      }

      // Ensure conversation is joined/subscribed to receive real-time updates
      try {
        // Check if conversation is already in subscribed list
        const subscribedConversationsPaginator = await client.getSubscribedConversations();
        const subscribedConversations = subscribedConversationsPaginator.items || [];
        const isSubscribed = subscribedConversations.some(c => c.sid === twilioSid);
        
        if (!isSubscribed) {
          console.log('[PatientChat] Conversation not subscribed, joining...');
          await twilioConv.join();
          console.log('[PatientChat] Conversation joined successfully');
        } else {
          console.log('[PatientChat] Conversation already subscribed');
        }
      } catch (joinError: any) {
        console.warn('[PatientChat] Failed to join conversation (may already be joined):', joinError.message);
        // Continue anyway - might already be joined
      }

      // Load existing messages from Twilio (getMessages returns newest first)
      const messagesPaginator = await twilioConv.getMessages(50);
      const twilioMessages: Message[] = messagesPaginator.items
        .map((msg: TwilioMessage) => ({
          id: msg.sid,
          body: msg.body || '',
          authorId: msg.author || '',
          authorType: (msg.author?.includes('patient') ? 'patient' : 'provider') as 'patient' | 'provider' | 'system',
          createdAt: msg.dateCreated?.toISOString() || new Date().toISOString(),
        }))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()); // Sort chronologically
      
      console.log('[PatientChat] Loaded', twilioMessages.length, 'existing messages');
      setMessages(twilioMessages);

      // Subscribe to new messages
      const messageHandler = (msg: TwilioMessage) => {
        console.log('[PatientChat] ===== MessageAdded event received =====', {
          sid: msg.sid,
          body: msg.body?.substring(0, 50),
          author: msg.author,
          dateCreated: msg.dateCreated,
          index: msg.index,
          conversationSid: twilioConv.sid,
        });

        const newMsg: Message = {
          id: msg.sid,
          body: msg.body || '',
          authorId: msg.author || '',
          authorType: (msg.author?.includes('patient') ? 'patient' : 'provider') as 'patient' | 'provider' | 'system',
          createdAt: msg.dateCreated?.toISOString() || new Date().toISOString(),
        };

        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === newMsg.id)) {
            console.log('[PatientChat] Duplicate message ignored:', newMsg.id);
            return prev;
          }
          console.log('[PatientChat] Adding new message to state:', {
            messageId: newMsg.id,
            body: newMsg.body.substring(0, 30),
            authorId: newMsg.authorId,
            currentMessageCount: prev.length,
          });
          // Insert in chronological order
          const updated = [...prev, newMsg].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          console.log('[PatientChat] Updated message count:', updated.length);
          return updated;
        });
      };

      // Subscribe to message events - use 'once' first to test, then 'on'
      console.log('[PatientChat] Setting up messageAdded listener...');
      twilioConv.on('messageAdded', messageHandler);
      
      // Also listen for all conversation-level events for debugging
      twilioConv.on('messageAdded', (msg) => {
        console.log('[PatientChat] [DEBUG] messageAdded fired:', msg.sid);
      });
      
      // Test: Try to manually fetch latest messages periodically to see if they exist
      const testMessageFetch = async () => {
        try {
          const latestMessages = await twilioConv.getMessages(10);
          console.log('[PatientChat] [TEST] Latest messages from Twilio:', {
            count: latestMessages.items.length,
            messages: latestMessages.items.map(m => ({
              sid: m.sid,
              body: m.body?.substring(0, 30),
              author: m.author,
              dateCreated: m.dateCreated,
            })),
          });
        } catch (err) {
          console.error('[PatientChat] [TEST] Failed to fetch messages:', err);
        }
      };
      
      // Test fetch after 5 seconds
      setTimeout(testMessageFetch, 5000);
      twilioConv.on('messageUpdated', (data: { message: TwilioMessage; updateReasons: any[] }) => {
        console.log('[PatientChat] MessageUpdated event:', data.message.sid);
      });
      twilioConv.on('messageRemoved', (msg: TwilioMessage) => {
        console.log('[PatientChat] MessageRemoved event:', msg.sid);
      });

      // Also listen for conversation updates
      twilioConv.on('updated', (data: { conversation: Conversation; updateReasons: any[] }) => {
        console.log('[PatientChat] Conversation updated:', data.updateReasons);
      });

      // Listen for participant changes to update avatar info
      const participantAddedHandler = async (participant: Participant) => {
        console.log('[PatientChat] Participant added:', participant.identity);
        try {
          const attrs = participant.attributes ? JSON.parse(participant.attributes) : {};
          setParticipants(prev => {
            const updated = new Map(prev);
            updated.set(participant.identity || '', {
              identity: participant.identity || '',
              attributes: attrs,
            });
            return updated;
          });
        } catch (e) {
          console.warn('[PatientChat] Failed to parse participant attributes:', e);
        }
      };

      const participantRemovedHandler = (participant: Participant) => {
        console.log('[PatientChat] Participant removed:', participant.identity);
        setParticipants(prev => {
          const updated = new Map(prev);
          updated.delete(participant.identity || '');
          return updated;
        });
      };

      twilioConv.on('participantAdded', participantAddedHandler);
      twilioConv.on('participantRemoved', participantRemovedHandler);

      console.log('[PatientChat] Subscribed to conversation events', {
        conversationSid: twilioConv.sid,
        hasMessageHandler: true,
      });

      // Set up periodic polling as fallback if real-time events don't fire
      // This helps catch messages that might not trigger events
      const pollingInterval = setInterval(async () => {
        if (twilioConversationRef.current) {
          try {
            const latestMessages = await twilioConversationRef.current.getMessages(10);
            const latestMessage = latestMessages.items[0]; // Most recent
            
            if (latestMessage) {
              setMessages(prev => {
                const exists = prev.some(m => m.id === latestMessage.sid);
                if (!exists) {
                  console.log('[PatientChat] [POLLING] Found new message via polling:', latestMessage.sid);
                  const newMsg: Message = {
                    id: latestMessage.sid,
                    body: latestMessage.body || '',
                    authorId: latestMessage.author || '',
                    authorType: (latestMessage.author?.includes('patient') ? 'patient' : 'provider') as 'patient' | 'provider' | 'system',
                    createdAt: latestMessage.dateCreated?.toISOString() || new Date().toISOString(),
                  };
                  return [...prev, newMsg].sort(
                    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                  );
                }
                return prev;
              });
            }
          } catch (err) {
            console.error('[PatientChat] [POLLING] Error polling messages:', err);
          }
        }
      }, 5000); // Poll every 5 seconds

      // Store polling interval in cleanup
      const originalCleanup = cleanupRef.current;
      cleanupRef.current = () => {
        clearInterval(pollingInterval);
        if (originalCleanup) originalCleanup();
        twilioConv.off('messageAdded', messageHandler);
        twilioConv.off('typingStarted', typingStartHandler);
        twilioConv.off('typingEnded', typingEndHandler);
        client.shutdown();
      };

      // Subscribe to typing indicators
      const typingStartHandler = (participant: Participant) => {
        // Only show typing indicator for non-patient (provider)
        if (!participant.identity?.includes('patient')) {
          setTypingIndicator(true);
        }
      };

      const typingEndHandler = (participant: Participant) => {
        if (!participant.identity?.includes('patient')) {
          setTypingIndicator(false);
        }
      };

      twilioConv.on('typingStarted', typingStartHandler);
      twilioConv.on('typingEnded', typingEndHandler);

      // Cleanup function is set up above with polling interval

      setConnectionStatus('connected');
      setReconnectAttempt(0); // Reset on successful connection
      console.log('[PatientChat] Twilio real-time connected');
      return true;
    } catch (err) {
      console.error('[PatientChat] Twilio initialization failed:', err);
      setConnectionStatus('failed');
      return false;
    }
  }, [token, mapConnectionState]);

  // Schedule auto-reconnect with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) return; // Already scheduled
    if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[PatientChat] Max reconnect attempts reached');
      setConnectionStatus('failed');
      return;
    }

    const delay = RECONNECT_DELAYS[reconnectAttempt] || RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1];
    console.log(`[PatientChat] Scheduling reconnect attempt ${reconnectAttempt + 1} in ${delay}ms`);
    
    setConnectionStatus('reconnecting');
    setReconnectAttempt(prev => prev + 1);

    reconnectTimeoutRef.current = setTimeout(async () => {
      reconnectTimeoutRef.current = null;
      
      // Cleanup previous connection
      cleanupTwilio();
      
      // Attempt reconnection
      if (twilioSidRef.current) {
        const success = await initializeTwilio(twilioSidRef.current);
        if (!success) {
          scheduleReconnect(); // Try again
        }
      }
    }, delay);
  }, [reconnectAttempt, cleanupTwilio, initializeTwilio]);

  // Manual retry button handler
  const handleRetryConnection = useCallback(async () => {
    setReconnectAttempt(0);
    cleanupTwilio();
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (twilioSidRef.current) {
      await initializeTwilio(twilioSidRef.current);
    }
  }, [cleanupTwilio, initializeTwilio]);

  // Validate token and load initial data
  useEffect(() => {
    if (!token) {
      setError('No access token provided.');
      setLoading(false);
      return;
    }

    const validateAndLoad = async () => {
      try {
        setLoading(true);
        setError(null);

        // Validate token
        const validateRes = await fetch(`${API_URL}/api/messaging/patient/${token}/validate`);
        const validateData = await validateRes.json();

        if (!validateRes.ok) {
          setErrorCode(validateData.error);
          setError(validateData.message);
          setLoading(false);
          return;
        }

        setConversation(validateData.conversation);
        setSession(validateData.session);

        // Check if real-time messaging is available
        if (!validateData.conversation?.twilioSid) {
          setErrorCode('REALTIME_UNAVAILABLE');
          const twilioAvailable = validateData.twilioAvailable !== false;
          if (twilioAvailable) {
            setError('Real-time messaging is not available for this conversation. The conversation may need to be set up. Please contact your clinic or try refreshing the page.');
          } else {
            setError('Real-time messaging is not configured. Please contact your clinic.');
          }
          setLoading(false);
          return;
        }

        // Initialize Twilio for real-time messaging (required)
        const twilioSuccess = await initializeTwilio(validateData.conversation.twilioSid);
        
        if (!twilioSuccess) {
          // Will auto-reconnect, but show the UI
          setConnectionStatus('failed');
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to load chat:', err);
        setError('Unable to connect. Please try again.');
        setLoading(false);
      }
    };

    validateAndLoad();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      cleanupTwilio();
    };
  }, [token, initializeTwilio, cleanupTwilio]);

  // Send message (Twilio only)
  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    // Check connection before sending
    if (connectionStatus !== 'connected' || !twilioConversationRef.current) {
      console.warn('[PatientChat] Cannot send - not connected:', {
        connectionStatus,
        hasConversation: !!twilioConversationRef.current,
      });
      alert('Not connected. Please wait for the connection to be restored.');
      return;
    }

    setSending(true);
    const messageBody = newMessage.trim();
    setNewMessage('');

    try {
      console.log('[PatientChat] Sending message via Twilio:', messageBody.substring(0, 50));
      const messageIndex = await twilioConversationRef.current.sendMessage(messageBody);
      console.log('[PatientChat] Message sent successfully, index:', messageIndex);
      // Message will be added via the real-time subscription (messageAdded event)
    } catch (err) {
      console.error('[PatientChat] Failed to send message:', err);
      setNewMessage(messageBody);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Manual refresh messages
  const refreshMessages = useCallback(async () => {
    if (!twilioConversationRef.current) {
      console.warn('[PatientChat] Cannot refresh - no conversation');
      return;
    }

    setRefreshing(true);
    try {
      console.log('[PatientChat] Manually refreshing messages...');
      const messagesPaginator = await twilioConversationRef.current.getMessages(50);
      const twilioMessages: Message[] = messagesPaginator.items
        .map((msg: TwilioMessage) => ({
          id: msg.sid,
          body: msg.body || '',
          authorId: msg.author || '',
          authorType: (msg.author?.includes('patient') ? 'patient' : 'provider') as 'patient' | 'provider' | 'system',
          createdAt: msg.dateCreated?.toISOString() || new Date().toISOString(),
        }))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      
      console.log('[PatientChat] Refreshed messages:', twilioMessages.length);
      setMessages(twilioMessages);
    } catch (err) {
      console.error('[PatientChat] Failed to refresh messages:', err);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Send typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator if connected via Twilio
    if (twilioConversationRef.current && connectionStatus === 'connected') {
      twilioConversationRef.current.typing();
    }
  };

  const requestNewLink = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/patient/${token}/refresh`, {
        method: 'POST',
      });

      const data = await res.json();

      if (res.ok) {
        alert('A new link has been sent to your email/phone.');
      } else {
        alert(data.message || 'Failed to send new link.');
      }
    } catch (err) {
      alert('Failed to send new link. Please contact your clinic.');
    } finally {
      setRefreshing(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get connection status display
  const getConnectionDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: Wifi, color: 'text-green-600', label: 'Connected', showRetry: false };
      case 'connecting':
        return { icon: Wifi, color: 'text-yellow-600', label: 'Connecting...', showRetry: false };
      case 'reconnecting':
        return { icon: RefreshCw, color: 'text-yellow-600', label: `Reconnecting (${reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})...`, showRetry: false };
      case 'disconnected':
        return { icon: WifiOff, color: 'text-gray-400', label: 'Disconnected', showRetry: true };
      case 'failed':
        return { icon: WifiOff, color: 'text-red-500', label: 'Connection failed', showRetry: true };
      default:
        return { icon: Wifi, color: 'text-gray-400', label: '', showRetry: false };
    }
  };

  const connectionDisplay = getConnectionDisplay();
  const ConnectionIcon = connectionDisplay.icon;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your conversation...</p>
        </div>
      </div>
    );
  }

  // Error state - expired token
  if (errorCode === 'TOKEN_EXPIRED') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-2">
            <Clock className="h-12 w-12 text-amber-500 mx-auto mb-2" />
            <h1 className="text-xl font-semibold">Link Expired</h1>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-6">
              This link has expired for your security. We can send you a new one.
            </p>
            <Button 
              onClick={requestNewLink} 
              disabled={refreshing}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Send New Link
            </Button>
            <p className="text-xs text-gray-500 mt-4">
              A new link will be sent to your registered email or phone.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - real-time unavailable
  if (errorCode === 'REALTIME_UNAVAILABLE') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-2">
            <WifiOff className="h-12 w-12 text-amber-500 mx-auto mb-2" />
            <h1 className="text-xl font-semibold">Real-time Unavailable</h1>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">
              Real-time messaging is not available for this conversation.
            </p>
            <p className="text-sm text-gray-500">
              Please contact your clinic for assistance or request a new chat link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - other errors
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-2">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
            <h1 className="text-xl font-semibold">Unable to Access</h1>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Please contact your clinic if you need assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main chat interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">Your Care Team</h1>
              <p className="text-xs text-gray-500">Secure messaging</p>
            </div>
          </div>
          
          {/* Connection status indicator */}
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5">
              <ConnectionIcon className={`h-3.5 w-3.5 ${connectionDisplay.color} ${connectionStatus === 'reconnecting' ? 'animate-spin' : ''}`} />
              <span className={connectionDisplay.color}>{connectionDisplay.label}</span>
            </div>
            {connectionDisplay.showRetry && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRetryConnection}
                className="h-6 px-2 text-xs"
              >
                Retry
              </Button>
            )}
            {twilioConversationRef.current && (
              <Button
                size="sm"
                variant="ghost"
                onClick={refreshMessages}
                disabled={refreshing}
                className="h-6 px-2 text-xs"
                title="Refresh messages"
              >
                {refreshing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Session expiring warning */}
      {session?.expiringSoon && (
        <div className="max-w-2xl mx-auto w-full px-4 pt-3 flex-shrink-0">
          <Alert className="bg-amber-50 border-amber-200">
            <Clock className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 flex items-center justify-between">
              <span>Your session expires soon.</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={requestNewLink}
                disabled={refreshing}
                className="ml-2"
              >
                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Get New Link'}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Connection error banner */}
      {(connectionStatus === 'failed' || connectionStatus === 'disconnected') && !loading && (
        <div className="max-w-2xl mx-auto w-full px-4 pt-3 flex-shrink-0">
          <Alert className="bg-red-50 border-red-200">
            <WifiOff className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 flex items-center justify-between">
              <span>
                {connectionStatus === 'failed' 
                  ? 'Connection failed. Messages may not be delivered.' 
                  : 'You are disconnected. Trying to reconnect...'}
              </span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleRetryConnection}
                className="ml-2 border-red-300 text-red-700 hover:bg-red-100"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Reconnecting banner */}
      {connectionStatus === 'reconnecting' && (
        <div className="max-w-2xl mx-auto w-full px-4 pt-3 flex-shrink-0">
          <Alert className="bg-yellow-50 border-yellow-200">
            <RefreshCw className="h-4 w-4 text-yellow-600 animate-spin" />
            <AlertDescription className="text-yellow-800">
              Reconnecting... Attempt {reconnectAttempt} of {MAX_RECONNECT_ATTEMPTS}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 pt-4 pb-2">
        <Card className="flex flex-col">
          <div className="p-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                <MessageSquare className="h-12 w-12 mb-4 opacity-30" />
                <p className="font-medium">No messages yet</p>
                <p className="text-sm">Send a message to your care team</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, index) => {
                  const isPatient = msg.authorType === 'patient';
                  const showDate = index === 0 || 
                    formatDate(msg.createdAt) !== formatDate(messages[index - 1].createdAt);
                  
                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <div className="text-center text-xs text-gray-400 py-2">
                          {formatDate(msg.createdAt)}
                        </div>
                      )}
                      <div className={`flex ${isPatient ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex gap-2 max-w-[80%] ${isPatient ? 'flex-row-reverse' : ''}`}>
                          {/* Care Team Avatar */}
                          {!isPatient && (() => {
                            // Get provider initials from participant info or authorId
                            let providerInitials = 'DR';
                            const participant = msg.authorId ? participants.get(msg.authorId) : null;
                            
                            if (participant?.attributes) {
                              const attrs = participant.attributes;
                              if (attrs.name) {
                                providerInitials = attrs.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                              } else if (attrs.firstName || attrs.lastName) {
                                providerInitials = `${attrs.firstName?.[0] || ''}${attrs.lastName?.[0] || ''}`.toUpperCase() || 'DR';
                              }
                            } else if (msg.authorId) {
                              // Try to extract from authorId format: "tenant:{tenantId}|user:{userId}"
                              // For now, use "DR" as default, but could be enhanced to fetch provider name
                              providerInitials = 'DR';
                            }
                            
                            return (
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">
                                  {providerInitials}
                                </AvatarFallback>
                              </Avatar>
                            );
                          })()}
                          {/* Patient Avatar */}
                          {isPatient && (
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                                {conversation?.patientName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'ME'}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div>
                            <div
                              className={`rounded-2xl px-4 py-2 ${
                                isPatient
                                  ? 'bg-teal-600 text-white rounded-br-md'
                                  : 'bg-gray-100 text-gray-900 rounded-bl-md'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                            </div>
                            <p className={`text-xs text-gray-400 mt-1 ${isPatient ? 'text-right' : ''}`}>
                              {formatTime(msg.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Typing indicator */}
          {typingIndicator && (
            <div className="px-4 py-2 text-sm text-gray-500 animate-pulse">
              <span className="inline-flex items-center gap-1">
                <span className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span className="ml-1">Care team is typing...</span>
              </span>
            </div>
          )}

          {/* Input */}
          <div className="px-3 pt-3 pb-2 border-t bg-gray-50 sticky bottom-0 z-10">
            <div className="flex gap-2">
              <Input
                placeholder={connectionStatus === 'connected' ? 'Type your message...' : 'Waiting for connection...'}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                disabled={sending || connectionStatus !== 'connected'}
                className="flex-1"
              />
              <Button 
                onClick={sendMessage} 
                disabled={sending || !newMessage.trim() || connectionStatus !== 'connected'}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-1.5 text-center text-xs text-gray-400">
        <p>{getFooterText()}</p>
      </footer>
    </div>
  );
};

export default PatientChat;
